import * as React from 'react';
import { cn } from '@/utils/utils';
import { FOCUS, SELECTED } from '@/components/ui/recipes';

interface TileProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  label: React.ReactNode;
  /** A line under the label. */
  description?: React.ReactNode;
  /** A glyph or preview: above the label, or before it in a row. */
  glyph?: React.ReactNode;
  /** A picture filling the tile's top edge to edge (a map, a cover). */
  media?: React.ReactNode;
  /** In a row, what sits at its end: a badge, a check, a count. */
  trailing?: React.ReactNode;
  selected?: boolean;
  /** sm for a dense grid of options, md for a choice with a description. */
  size?: 'sm' | 'md';
  /** column: glyph over the label, centred (a grid of choices); row: glyph, text and trailing side by side (a list of choices); split: glyph and text in its first half, trailing in its second, a line between (a story and its version, in the publish flow). */
  layout?: 'column' | 'row' | 'split';
  /** div: a tile holding controls of its own (a select), whose owner gives it its role and keys and ignores clicks while it's disabled (a sortable row). */
  as?: 'button' | 'div';
  /** emphasis: on a deep fill (the hero band's quick actions), frosted glass in a medium line of the light ink. */
  tone?: 'default' | 'emphasis';
}

// A tile on a deep fill: frosted glass in a medium line of the light ink, its line full on hover
const EMPHASIS = 'h-20 gap-1.5 rounded-lureo-radius-lg border-lureo-border-width-medium border-lureo-on-emphasis/40 bg-lureo-on-emphasis/10 px-2 text-lureo-16 text-lureo-on-emphasis backdrop-blur-lureo-blur-sm hover:border-lureo-on-emphasis hover:bg-lureo-on-emphasis/15 sm:text-lureo-14';

/** Lureo's tile: one option in a grid or list of choices (a shape, a behaviour, a platform), selected when chosen. */
export const Tile = React.forwardRef<HTMLButtonElement, TileProps>(
  ({ label, description, glyph, media, trailing, selected = false, disabled = false, size = 'sm', layout = 'column', as = 'button', tone = 'default', className, type = 'button', ...props }, ref) => {
    const text = (
      <>
        <span className={cn('font-medium leading-[1.3]', layout === 'split' && 'truncate')}>{label}</span>
        {description && <span className={cn('font-lureo-font-prose text-lureo-12 leading-[1.4]', selected ? 'text-lureo-brand-ink' : 'text-lureo-ink-subtle')}>{description}</span>}
      </>
    );
    // Typed as a button either way; a div leaves out the attributes only a button has
    const Root = as as 'button';
    return (
      <Root
        ref={ref}
        type={as === 'button' ? type : undefined}
        disabled={as === 'button' ? disabled : undefined}
        aria-disabled={as === 'div' && disabled ? true : undefined}
        aria-pressed={selected}
        className={cn(
          'flex w-full rounded-lureo-radius border-lureo-border-width font-lureo-font-ui transition-colors',
          media
            ? 'flex-col overflow-hidden text-left text-lureo-13'
            : layout === 'row'
              ? 'min-h-14 items-center gap-3 px-3 py-2.5 text-left text-lureo-14'
              : layout === 'split'
                ? 'relative grid grid-cols-1 items-stretch overflow-hidden rounded-lureo-radius-lg text-left text-lureo-15 sm:grid-cols-2'
                : size === 'sm'
                ? 'min-h-14 flex-col items-center justify-center gap-1 px-1.5 py-2.5 text-lureo-13'
                : 'min-h-20 flex-col items-start justify-center gap-1 px-3 py-3 text-left text-lureo-14',
          FOCUS,
          disabled ? 'cursor-not-allowed border-lureo-line bg-lureo-surface text-lureo-ink opacity-lureo-disabled-opacity' : selected ? SELECTED : 'border-lureo-line bg-lureo-surface text-lureo-ink hover:border-lureo-brand-ink',
          tone === 'emphasis' && EMPHASIS,
          className,
        )}
        {...props}
      >
        {media ? (
          <>
            <span className="block h-24 w-full shrink-0 bg-lureo-neutral [&>img]:h-full [&>img]:w-full [&>img]:object-cover">{media}</span>
            <span className="flex flex-col gap-0.5 px-3 py-2">{text}</span>
          </>
        ) : layout === 'split' ? (
          <>
            {/* Side by side from sm, a line between; on a phone the halves stack, the line over the second */}
            <span aria-hidden className="absolute bottom-3 left-1/2 top-3 hidden w-px -translate-x-1/2 bg-lureo-line sm:block" />
            <span className="flex min-w-0 items-center gap-2.5 py-2.5 pl-4 pr-4">
              {glyph && <span className="inline-flex shrink-0 items-center">{glyph}</span>}
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">{text}</span>
            </span>
            <span className="flex min-w-0 items-center border-t-lureo-border-width border-lureo-line py-2.5 pl-4 pr-3.5 sm:border-t-0">{trailing}</span>
          </>
        ) : layout === 'row' ? (
          <>
            {glyph && <span className="inline-flex shrink-0 items-center justify-center">{glyph}</span>}
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">{text}</span>
            {trailing && <span className="shrink-0">{trailing}</span>}
          </>
        ) : (
          <>
            {glyph && <span className="inline-flex items-center justify-center">{glyph}</span>}
            {text}
          </>
        )}
      </Root>
    );
  },
);
Tile.displayName = 'Tile';
